import { useState, type ReactNode } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { Popover, PopoverContent, PopoverTrigger } from '@unitfield/ui/components/ui/popover';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { usePublicShell } from '../shell/public-brand-provider';
import { DateCalendar, SheetPopover } from '../shell/date-well';
import calendarStyles from '../shell/calendar.module.css';
import {
  BEDS_CHOICES,
  bedsTokenLabel,
  formatRent,
  formatShortDate,
  homesCountLabel,
  parseIsoDate,
  priceClause,
  priceRangeLabel,
  priceSliderValue,
  rangeFromPriceSlider,
  toIsoDate,
  type BedsChoice,
  type ListingSearch,
  type PriceBounds,
  type RentRange,
} from './listing-model';
import styles from './listing.module.css';

// The kit popover brings a dashboard surface; the sentence draws its own card inside the shell.
const POPOVER_RESET = 'w-auto rounded-none border-0 bg-transparent p-0 shadow-none text-inherit';

type TokenSurface = 'choices' | 'calendar' | 'price';

function Token({
  label,
  open,
  onOpenChange,
  children,
  ariaLabel,
  surface = 'choices',
}: {
  label: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  ariaLabel: string;
  surface?: TokenSurface;
}) {
  const { container } = usePublicShell();
  const trigger = (
    <button type="button" className={styles.token} data-open={open ? '' : undefined} aria-label={ariaLabel}>
      <span className={`${styles.num} ${styles.tokenLabel}`}>{label}</span>
      <UnitfieldIcon name="chevron-down" className={styles.tokenIcon} aria-hidden="true" />
    </button>
  );
  if (surface === 'calendar') return <SheetPopover open={open} onOpenChange={onOpenChange} trigger={trigger} label="Choose move-in date">{children}</SheetPopover>;
  if (surface === 'price') {
    return (
      <SheetPopover
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        label="Choose a price range"
        popover={(content) => container ? (
          <PopoverContent container={container} align="start" sideOffset={10} collisionPadding={12} className={POPOVER_RESET} aria-label="Choose a price range">
            <div className={`${styles.popover} ${styles.pricePopover}`}>{content}</div>
          </PopoverContent>
        ) : null}
      >
        {children}
      </SheetPopover>
    );
  }
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {trigger}
      </PopoverTrigger>
      <PopoverContent container={container} align="start" sideOffset={10} collisionPadding={12} className={POPOVER_RESET}>
        <div className={styles.popover}>{children}</div>
      </PopoverContent>
    </Popover>
  );
}

function Choice({ selected, onSelect, children, className = '' }: { selected: boolean; onSelect: () => void; children: ReactNode; className?: string }) {
  return (
    <button type="button" className={`${styles.choice} ${className}`} aria-pressed={selected} onClick={onSelect}>
      {children}
    </button>
  );
}

/**
 * The price card: a live readout, the two-thumb slider and its footer. The thumbs move locally;
 * the URL changes once, on release, and the card stays open.
 */
function PriceRange({
  range,
  bounds,
  countMatches,
  onChange,
  onReset,
  onClose,
}: {
  range: RentRange;
  bounds: PriceBounds;
  /** Homes that fit a range with the other filters; null when the client cannot know (server paging). */
  countMatches: ((range: RentRange) => number) | null;
  onChange: (range: RentRange) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const shown = priceSliderValue(range, bounds);
  const [draft, setDraft] = useState<number[]>(shown);
  // A committed change from outside (reset, a shared link) moves the thumbs too.
  const committed = `${range.rentMin}:${range.rentMax}`;
  const [seen, setSeen] = useState(committed);
  if (seen !== committed) {
    setSeen(committed);
    setDraft(shown);
  }
  const draftRange = rangeFromPriceSlider(draft, bounds, range);
  const count = countMatches ? countMatches(draftRange) : null;
  const show = count === null ? 'Show homes' : count === 0 ? 'Close' : `Show ${homesCountLabel(count)}`;

  return (
    <>
      <div className={styles.priceReadout}>
        <span className={`${styles.num} ${styles.priceValue}`}>{priceRangeLabel(draftRange)}</span>
        {count === null ? null : <span className={`${styles.num} ${styles.priceCount}`}>{homesCountLabel(count)}</span>}
      </div>
      <SliderPrimitive.Root
        className={styles.slider}
        min={bounds.min}
        max={bounds.max}
        step={bounds.step}
        minStepsBetweenThumbs={1}
        value={draft}
        onValueChange={setDraft}
        onValueCommit={(value) => onChange(rangeFromPriceSlider(value, bounds, range))}
      >
        <SliderPrimitive.Track className={styles.sliderTrack}>
          <SliderPrimitive.Range className={styles.sliderRange} />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          className={styles.sliderThumb}
          aria-label="Minimum rent"
          aria-valuetext={draftRange.rentMin === null ? 'No minimum' : formatRent(draftRange.rentMin) ?? undefined}
        />
        <SliderPrimitive.Thumb
          className={styles.sliderThumb}
          aria-label="Maximum rent"
          aria-valuetext={draftRange.rentMax === null ? 'No maximum' : formatRent(draftRange.rentMax) ?? undefined}
        />
      </SliderPrimitive.Root>
      <div className={styles.priceFooter}>
        <button type="button" className={`${styles.choice} ${styles.priceReset}`} onClick={onReset}>
          Any price
        </button>
        <button type="button" className={styles.priceShow} onClick={onClose}>
          <span className={styles.num}>{show}</span>
        </button>
      </div>
    </>
  );
}

type OpenToken = 'beds' | 'price' | 'date' | null;

/**
 * The filter as the renter's own sentence: "I'm looking for [2+ bedrooms] from [$1,800 to $2,600],
 * moving in around [Nov 1]." Each token opens a small card. With fewer than two distinct rents
 * there is no price to choose, so the price clause is left out.
 */
export function ListingSentence({
  search,
  priceBounds,
  countMatches,
  today,
  onChange,
}: {
  search: ListingSearch;
  priceBounds: PriceBounds | null;
  countMatches: ((range: RentRange) => number) | null;
  today: Date;
  onChange: (next: Partial<ListingSearch>) => void;
}) {
  const [open, setOpen] = useState<OpenToken>(null);
  const toggle = (token: Exclude<OpenToken, null>) => (next: boolean) => setOpen(next ? token : null);
  const choose = (next: Partial<ListingSearch>) => {
    onChange(next);
    setOpen(null);
  };
  const moveIn = parseIsoDate(search.moveIn);
  const range: RentRange = { rentMin: search.rentMin, rentMax: search.rentMax };
  const price = priceClause(range);
  const beds = (
    <Token label={bedsTokenLabel(search.beds)} ariaLabel={`Bedrooms: ${bedsTokenLabel(search.beds)}`} open={open === 'beds'} onOpenChange={toggle('beds')}>
      {BEDS_CHOICES.map((choice) => (
        <Choice key={choice.label} selected={search.beds === choice.value} onSelect={() => choose({ beds: choice.value as BedsChoice | null })}>
          {choice.label}
        </Choice>
      ))}
    </Token>
  );

  return (
    <p className={styles.sentence}>
      I&rsquo;m looking for{' '}
      {priceBounds ? (
        <>
          {beds}{search.beds === null ? ' home' : ''} {price.connective}{' '}
          {/* Punctuation stays on the token's line. */}
          <span className={styles.bind}>
          <Token label={price.label} ariaLabel={`Price: ${price.label}`} open={open === 'price'} onOpenChange={toggle('price')} surface="price">
            <PriceRange
              range={range}
              bounds={priceBounds}
              countMatches={countMatches}
              onChange={onChange}
              onReset={() => choose({ rentMin: null, rentMax: null })}
              onClose={() => setOpen(null)}
            />
          </Token>,
          </span>
        </>
      ) : search.beds === null ? (
        <>{beds} home,</>
      ) : (
        <span className={styles.bind}>{beds},</span>
      )}{' '}
      moving in{moveIn ? ' around' : ''}{' '}
      <span className={styles.bind}>
      <Token
        label={moveIn ? formatShortDate(moveIn, today) : 'any time'}
        ariaLabel={`Move-in date: ${moveIn ? formatShortDate(moveIn, today) : 'any time'}`}
        open={open === 'date'}
        onOpenChange={toggle('date')}
        surface="calendar"
      >
        <DateCalendar
          label="Move-in date"
          value={search.moveIn}
          min={toIsoDate(today)}
          onSelect={(value) => choose({ moveIn: value })}
        />
        <Choice selected={!moveIn} onSelect={() => choose({ moveIn: null })} className={calendarStyles.clearChoice}>
          <span className={calendarStyles.clearLabel}>Any time</span>
          <span className={calendarStyles.sheetClearLabel}>Clear date</span>
        </Choice>
      </Token>.
      </span>
    </p>
  );
}
